/**
 * Whether the burned-in PDF still matches the editor's redactions (VE-25870,
 * VE-26422).
 *
 * Decided by matching version numbers, as audio/video is. The export works out
 * which version it is about to produce, writes that onto the
 * `document-redaction-data` it consumes, and the verdict compares it against the
 * finished output's `version`. Both sides carry the number by agreement, so
 * neither has to observe the other and the order the assets land in stops
 * mattering. The session carries the label as `save.lastSavedBurnInVersion`.
 *
 * Its three states are the whole point. A number means the newest save was sent
 * for burn-in and says which version; `null` means it was an ordinary save that
 * was never sent, which is evidence the output is behind without anything to
 * compare; `undefined` means the save predates labelling and only the counts
 * below can judge it.
 *
 * A save with no label — written before this scheme — falls back to timestamps:
 * did the newest save land after the output was created? That needs no tolerance
 * and no size gate, unlike the audio/video equivalent, because documents have
 * never had the race those fences exist for. The export has forced a save and
 * waited on it before rendering since VE-20838 (2026-06-08), two days after the
 * burn-in feature itself, so the consuming save always completes before the
 * render starts and the output is created only once it finishes.
 *
 * The gap it accepts: a save made while the burn-in was still running lands
 * before the output and reads as included. Accepted — PDF burn-in is quick, and
 * the editor shows its own progress state throughout.
 *
 * The dirty flag is an input here and deliberately isn't for audio/video: every
 * action marking a document session dirty is a redaction mutation, so it never
 * fires on a stray selection. VE-25718: "dirty" here means `hasUnsavedWork` —
 * a FAILED save holds edits the server does not have just as surely as an
 * un-saved one, and reading it as clean is how the banner comes to call a
 * genuinely behind output current.
 */
import {
  computeRedactedFileStaleness,
  NOT_STALE,
  RedactedFileStaleness,
} from '@helpers/redactedFileStaleness';

import { hasUnsavedWork, SessionState } from './models';

/** Phases in which a newer output is on its way. */
const BURN_IN_PHASES = new Set(['preparing', 'burning', 'uploading']);

export const getDocumentStaleness = (
  session: SessionState | null | undefined
): RedactedFileStaleness => {
  const latest = session?.versionHistory?.[session.versionHistory.length - 1];
  if (!session || !latest?.redactedAssetUrl) {
    return NOT_STALE;
  }

  /**
   * VE-26422: the newest save says which output version it was sent to produce,
   * and the output says which version it is. Equal means the file on disk was
   * built from the data we have. `null` means the save was never sent for
   * burn-in at all, which is evidence on its own. `undefined` means the save
   * predates labelling, and only the counts below can judge it.
   */
  const sentForVersion = session.save.lastSavedBurnInVersion;
  const outputIsBehind = (() => {
    // Never sent for burn-in: not in any output, whatever it contains.
    if (sentForVersion === null) {
      return true;
    }
    if (
      typeof sentForVersion === 'number' &&
      typeof latest.version === 'number'
    ) {
      return sentForVersion !== latest.version;
    }
    // No label — a save written before this scheme. Did anything land after the
    // output existed? See the header for why this needs no tolerance.
    const savedAt = session.save.lastSavedAt;
    return (
      !!savedAt &&
      !!latest.redactedAt &&
      new Date(savedAt).getTime() > new Date(latest.redactedAt).getTime()
    );
  })();

  return computeRedactedFileStaleness({
    hasOutput: true,
    savedAfterBurnIn: outputIsBehind,
    // Sound for documents; see the header.
    // VE-25718: `hasUnsavedWork` covers dirty AND error — a failed save leaves
    // the edits in memory and nowhere else, so the output cannot be current.
    // `saving` is added on top because it is unsaved for THIS purpose too: the
    // data is not on the server yet. (hasUnsavedWork excludes `saving` because
    // its own question is "should a save be startable", and one is in flight.)
    hasUnsavedChanges:
      hasUnsavedWork(session.save) || session.save.status === 'saving',
    // Burn-in runs in this tab rather than as an engine task, so the phase the
    // export saga publishes is the only signal there is.
    isBurnInRunning: BURN_IN_PHASES.has(session.export.phase),
    version: latest.version ?? undefined,
  });
};
